<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, provide, readonly } from 'vue'
import { useToast } from 'vue-toastification'
import { useMainStore } from '@/stores/main'
import { useDashboardStore } from '@/stores/dashboard'
import { useGlobalDashboardSettings } from '@/composables/useGlobalDashboardSettings'
import DashboardGrid from '@/components/dashboard/DashboardGrid.vue'
import DashboardSelector from '@/components/dashboard/DashboardSelector.vue'
import TimeRangeOverrideDropdown from '@/components/dashboard/TimeRangeOverrideDropdown.vue'
import RefreshIntervalOverrideDropdown from '@/components/dashboard/RefreshIntervalOverrideDropdown.vue'
import VisibilityDropdown from '@/components/dashboard/VisibilityDropdown.vue'
import WidgetSlideover from '@/components/dashboard/WidgetSlideover.vue'
import BrowseDashboardsSlideover from '@/components/dashboard/BrowseDashboardsSlideover.vue'
import ExportDashboardModal from '@/components/dashboard/ExportDashboardModal.vue'
import ImportDashboardModal from '@/components/dashboard/ImportDashboardModal.vue'
import ForkDashboardModal from '@/components/dashboard/ForkDashboardModal.vue'
import DeleteDashboardModal from '@/components/dashboard/DeleteDashboardModal.vue'

import { useUserStore } from '@/stores/user'

const toast = useToast()
const mainStore = useMainStore()
const dashboardStore = useDashboardStore()
const userStore = useUserStore()

const hasEventAccess = computed(() => {
  return userStore.accountType >= 4 || userStore.accountType === 1337
})

// Global dashboard settings
const {
  globalRefreshInterval,
  hasRefreshOverride
} = useGlobalDashboardSettings()

// Global refresh trigger (provided to widgets)
const globalRefreshTrigger = ref(0)
provide('globalRefreshTrigger', readonly(globalRefreshTrigger))

// Local state
const isCreating = ref(false)
const isDeleting = ref(false)
const isLocked = ref(true) // Default to locked
const isInitialLoad = ref(true) // Track if we're in initial dashboard load

// Computed properties
const dashboards = computed(() => dashboardStore.dashboards)
const activeDashboard = computed(() => dashboardStore.activeDashboard)
const widgets = computed(() => dashboardStore.widgets)
const isLoadingList = computed(() => dashboardStore.dashboardsLoading)
const isLoadingDashboard = computed(() => dashboardStore.activeDashboardLoading)
const hasDashboards = computed(() => dashboardStore.hasDashboards)
const dashboardError = computed(() => dashboardStore.activeDashboardError)
const isSubscribed = computed(() => activeDashboard.value?.relationship === 'subscribed')

// Global refresh interval timer
let globalRefreshIntervalTimer = null

function setupGlobalRefresh() {
  clearGlobalRefresh()

  if (hasRefreshOverride.value && globalRefreshInterval.value !== 'per_widget') {
    const intervalMs = parseInt(globalRefreshInterval.value) * 1000
    globalRefreshIntervalTimer = setInterval(() => {
      globalRefreshTrigger.value++
    }, intervalMs)
  }
}

function clearGlobalRefresh() {
  if (globalRefreshIntervalTimer) {
    clearInterval(globalRefreshIntervalTimer)
    globalRefreshIntervalTimer = null
  }
}

// Watch for changes to global refresh setting
watch([globalRefreshInterval, hasRefreshOverride], () => {
  setupGlobalRefresh()
})

// Handle tab visibility - pause refresh when tab is not visible
function handleVisibilityChange() {
  if (document.hidden) {
    clearGlobalRefresh()
  } else {
    setupGlobalRefresh()
  }
}

// On mount, fetch dashboards and saved searches
onMounted(async () => {
  await Promise.all([
    dashboardStore.fetchDashboards(),
    dashboardStore.fetchSavedSearches()
  ])

  // Auto-select the first dashboard if available
  if (dashboards.value.length > 0 && !activeDashboard.value) {
    await selectDashboard(dashboards.value[0].id)
  }

  // Setup global refresh interval
  setupGlobalRefresh()

  // Setup visibility change listener
  document.addEventListener('visibilitychange', handleVisibilityChange)
})

// Cleanup on unmount
onBeforeUnmount(() => {
  clearGlobalRefresh()
  document.removeEventListener('visibilitychange', handleVisibilityChange)
})

// Select a dashboard
async function selectDashboard(dashboardId) {
  if (dashboardId) {
    clearGlobalRefresh() // Clear before switching
    await dashboardStore.fetchDashboard(dashboardId)
    setupGlobalRefresh() // Restart after switching
  }
}

// Toggle lock state
function toggleLock() {
  isLocked.value = !isLocked.value
}

// Reset lock state when switching dashboards
watch(() => dashboardStore.activeDashboard?.id, (newId, oldId) => {
  if (newId && newId !== oldId) {
    isLocked.value = true // Reset to locked when switching dashboards
    isInitialLoad.value = true // Mark as initial load for new dashboard
  }
})

// Auto-unlock when widget is added (but not during initial load)
watch(() => widgets.value.length, (newLength, oldLength) => {
  // Mark initial load as complete after first widget load
  if (isInitialLoad.value) {
    isInitialLoad.value = false
    return // Don't unlock during initial load
  }

  // Only unlock if widgets increased (user added a widget)
  if (newLength > oldLength && isLocked.value) {
    isLocked.value = false // Unlock after adding widget
  }
})

// Create a new dashboard
async function createDashboard(name) {
  if (!name.trim()) {
    toast.error('Please enter a dashboard name')
    return
  }

  isCreating.value = true
  try {
    const newDashboard = await dashboardStore.createDashboard(name.trim())
    await selectDashboard(newDashboard.id)
    toast.success('Dashboard created')
  } catch (e) {
    toast.error(e.message || 'Failed to create dashboard')
  } finally {
    isCreating.value = false
  }
}

// Update dashboard name
async function updateDashboardName(dashboardId, name) {
  try {
    await dashboardStore.updateDashboard(dashboardId, { name: name.trim() })
    toast.success('Dashboard name updated')
  } catch (e) {
    toast.error(e.message || 'Failed to update dashboard')
  }
}

// Delete or unsubscribe a dashboard
async function deleteDashboard(dashboardId) {
  const dashboard = dashboards.value.find(d => d.id === dashboardId)
  const isSub = dashboard?.relationship === 'subscribed'

  if (isSub) {
    // Subscribed: just unsubscribe directly
    isDeleting.value = true
    try {
      await dashboardStore.unsubscribeFromDashboard(dashboardId)
      toast.success('Unsubscribed from dashboard')
      if (dashboards.value.length > 0) {
        await selectDashboard(dashboards.value[0].id)
      }
    } catch (e) {
      toast.error(e.message || 'Failed to unsubscribe')
    } finally {
      isDeleting.value = false
    }
    return
  }

  // Owned: show delete modal
  mainStore.show_modal(DeleteDashboardModal, {
    dashboard,
    onDelete: async (deleteSavedSearchIDs) => {
      await dashboardStore.deleteDashboard(dashboardId, deleteSavedSearchIDs)
      toast.success('Dashboard deleted')
      if (dashboards.value.length > 0) {
        await selectDashboard(dashboards.value[0].id)
      }
    }
  })
}

// Open add widget slideover
function openAddWidgetSlideover() {
  mainStore.show_slideover(WidgetSlideover, {}, { width: 40 }, 'Add Widget')
}

// Open widget configuration slideover
function openConfigSlideover(widget) {
  mainStore.show_slideover(WidgetSlideover, { widget }, { width: 40 }, 'Configure Widget')
}

// Handle widget deletion
async function handleDeleteWidget(widgetId) {
  if (!confirm('Are you sure you want to delete this widget?')) {
    return
  }

  try {
    await dashboardStore.deleteWidget(widgetId)
    toast.success('Widget deleted')
  } catch (e) {
    toast.error(e.message || 'Failed to delete widget')
  }
}

// Fork current subscribed dashboard into an owned copy
function handleFork() {
  if (!activeDashboard.value) return

  mainStore.show_modal(ForkDashboardModal, {
    dashboard: activeDashboard.value,
    isSubscribed: true,
    onFork: async (forked) => {
      if (forked?.id) {
        await selectDashboard(forked.id)
      }
      toast.success('Dashboard forked')
    }
  })
}

// Browse shared dashboards
function handleBrowse() {
  mainStore.show_slideover(
    BrowseDashboardsSlideover,
    {
      onFork: async (forked) => {
        if (forked?.id) {
          await selectDashboard(forked.id)
        }
      },
      onSubscribe: async (dashboard) => {
        // Auto-select the subscribed dashboard if none is active
        if (!activeDashboard.value || dashboardError.value) {
          await selectDashboard(dashboard.id)
        }
      }
    },
    { width: 40 },
    'Browse Dashboards'
  )
}

// Import dashboard via modal
function handleImport() {
  mainStore.show_modal(ImportDashboardModal, {
    onImport: async (dashboard) => {
      if (dashboard?.id) {
        await selectDashboard(dashboard.id)
      }
      toast.success('Dashboard imported')
    }
  })
}

// Export dashboard via modal
async function handleExport() {
  if (!activeDashboard.value) return

  try {
    const exportData = await dashboardStore.exportDashboard(activeDashboard.value.id)
    mainStore.show_modal(ExportDashboardModal, {
      dashboardName: activeDashboard.value.name,
      exportData
    })
  } catch (e) {
    toast.error(e.message || 'Failed to export dashboard')
  }
}

// Change dashboard sharing visibility
async function handleDashboardVisibility(value) {
  if (!activeDashboard.value) return

  try {
    await dashboardStore.setVisibility(activeDashboard.value.id, value)
    toast.success('Visibility updated')
  } catch (e) {
    toast.error(e.message || 'Failed to update visibility')
  }
}

</script>

<template>
  <div class="dashboards-page">
    <!-- Upgrade prompt for non-elite users -->
    <div v-if="!hasEventAccess" class="forbidden-page">
      <div class="forbidden-box">
        <font-awesome-icon :icon="['fas', 'lock']" class="forbidden-icon" />
        <span class="forbidden-message">Dashboards require an Elite or Enterprise subscription</span>
      </div>
    </div>
    <div v-else class="page-container">
      <!-- Header -->
      <div class="page-header">
        <div class="header-left">
          <DashboardSelector
            :dashboards="dashboards"
            :active-dashboard="activeDashboard"
            :loading="isLoadingList"
            @select="selectDashboard"
            @create="createDashboard"
            @update="updateDashboardName"
            @delete="deleteDashboard"
            @browse="handleBrowse"
            @import-dashboard="handleImport"
          />
          <TimeRangeOverrideDropdown />
          <RefreshIntervalOverrideDropdown />
          <VisibilityDropdown
            v-if="activeDashboard?.is_editable"
            :visibility="activeDashboard?.visibility ?? 0"
            @change="handleDashboardVisibility"
          />
        </div>

        <div class="header-right" v-if="activeDashboard && !dashboardError">
          <button
            class="btn-export"
            @click="handleExport"
            title="Export dashboard"
          >
            <font-awesome-icon :icon="['fas', 'file-export']" />
            Export
          </button>

          <template v-if="isSubscribed">
            <button
              class="btn-fork"
              @click="handleFork"
              title="Fork into your own editable copy"
            >
              <font-awesome-icon :icon="['fas', 'code-fork']" />
              Fork
            </button>
          </template>

          <template v-else>
            <button
              class="btn-lock"
              @click="toggleLock"
              :title="isLocked ? 'Unlock dashboard' : 'Lock dashboard'"
            >
              <font-awesome-icon :icon="isLocked ? 'lock' : 'lock-open'" />
              {{ isLocked ? 'Locked' : 'Unlocked' }}
            </button>

            <button
              class="btn-add-widget"
              @click="openAddWidgetSlideover"
            >
              <font-awesome-icon icon="plus" />
              Widget
            </button>
          </template>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="isLoadingDashboard" class="loading-container">
        <font-awesome-icon icon="spinner" class="spinner" spin />
        <p class="loading-text">Loading dashboard...</p>
      </div>

      <!-- Error State - Access denied / load failure -->
      <div v-else-if="dashboardError" class="forbidden-page">
        <div class="forbidden-box">
          <font-awesome-icon :icon="['fas', 'lock']" class="forbidden-icon" />
          <span class="forbidden-message">{{ dashboardError }}</span>
        </div>
      </div>

      <!-- Empty State - No Dashboards -->
      <div v-else-if="!hasDashboards" class="empty-state">
        <font-awesome-icon icon="table-cells-large" class="empty-icon" />
        <p class="empty-title">No dashboards yet</p>
        <p class="empty-description">Create your first dashboard using the selector above</p>
      </div>

      <!-- Dashboard Grid -->
      <div v-else-if="activeDashboard" class="dashboard-content">
        <DashboardGrid
          v-if="widgets.length > 0"
          :widgets="widgets"
          :is-locked="isLocked"
          @configure="openConfigSlideover"
          @delete="handleDeleteWidget"
        />

        <!-- Empty widget state -->
        <div v-else class="empty-widgets">
          <font-awesome-icon icon="puzzle-piece" class="empty-icon" />
          <p class="empty-title">No widgets yet</p>
          <p class="empty-description">Add widgets to visualize your saved search data</p>
          <button class="btn-add-first-widget" @click="openAddWidgetSlideover">
            <font-awesome-icon icon="plus" />
            Add Your First Widget
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.dashboards-page {
  width: 100%;
  height: 100%;
  background: #191f27;
  overflow-y: auto;
}

.page-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* Page Header */
.page-header {
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding: 0.75rem;
  background: #151a22;
  border-radius: 1rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

/* Header Actions */
.header-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.btn-export,
.btn-fork,
.btn-lock,
.btn-add-widget {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  background: transparent;
  border: none;
  border-radius: 0.75rem;
  color: #7A8696;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-export:hover,
.btn-fork:hover,
.btn-lock:hover,
.btn-add-widget:hover {
  background: #1d2531;
  color: white;
}

/* Loading State */
.loading-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
}

.spinner {
  font-size: 2.5rem;
  color: #7357D2;
  margin-bottom: 1rem;
}

.loading-text {
  color: #9ca3af;
  font-size: 0.875rem;
  margin: 0;
}

/* Empty States */
.empty-state,
.empty-widgets {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
}

.empty-icon {
  font-size: 3rem;
  color: #6b7280;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.empty-title {
  font-size: 1rem;
  font-weight: 600;
  color: #9ca3af;
  margin: 0 0 0.5rem 0;
}

.empty-description {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
  text-align: center;
}

.btn-add-first-widget {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: rgba(115, 87, 210, 0.15);
  border: 1px solid rgba(115, 87, 210, 0.3);
  border-radius: 0.5rem;
  color: #7357D2;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 1.5rem;
}

.btn-add-first-widget:hover {
  background: rgba(115, 87, 210, 0.25);
  border-color: rgba(115, 87, 210, 0.5);
}

/* Dashboard Content */
.dashboard-content {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

/* Responsive */
@media (max-width: 768px) {
  .page-container {
    padding: 1rem;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .header-left {
    width: 100%;
  }

  .header-right {
    width: 100%;
  }
}

/* Forbidden / upgrade prompt */
.forbidden-page {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.forbidden-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 2rem 3rem;
  background: #1a1f27;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
}

.forbidden-icon {
  font-size: 2rem;
  color: #6B7280;
  opacity: 0.6;
}

.forbidden-message {
  color: #E5E7EB;
  font-size: 0.95rem;
  text-align: center;
}

.upgrade-link {
  color: #7357D2;
  font-size: 0.9rem;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s;
}

.upgrade-link:hover {
  color: #9B7EE8;
}
</style>
